<script setup lang="ts">
/**
 * MichiganMitten — real cartographic silhouette of Michigan's Lower Peninsula.
 *
 * Path is the "Real cartographic outline" from the Up North Cottage brand kit
 * (foundations.jsx · MITTEN_PATH). It traces the true coastline — Saginaw Bay
 * notch, the Thumb, the Leelanau hook, and the smooth Lake-Michigan west shore —
 * so it reads unmistakably as Michigan at any size. viewBox is 311.4 x 400.2
 * (aspect ≈ 0.778 — height = 1.285 x width); scale via `size`.
 */
withDefaults(
  defineProps<{
    size?: number | string
    fill?: string
    stroke?: string
    strokeWidth?: number | string
    title?: string
  }>(),
  {
    size: 80,
    fill: 'currentColor',
    stroke: 'none',
    strokeWidth: 0,
    title: 'Michigan',
  },
)

// Lower Peninsula outline (cartographic trace), starting from the east shore.
const LP_PATH =
  'M311.2 275.1L311.2 278.9L307.4 286.5L306.6 294.1L307.6 298.6L303.7 313.4L299.0 318.9L295.4 318.4L292.1 321.9L293.7 319.3L292.0 315.2L290.3 314.3L292.3 313.2L291.9 311.5L296.1 310.1L296.1 307.1L291.1 305.5L283.2 310.2L282.8 312.6L286.3 314.8L284.9 315.2L285.4 317.5L281.7 317.2L278.9 321.5L279.2 328.4L277.5 334.3L275.4 337.9L265.5 341.1L263.2 343.8L261.0 348.7L260.6 363.0L256.7 365.1L257.9 370.0L252.4 374.0L251.3 378.1L249.7 376.7L247.7 377.5L245.9 382.8L243.0 383.6L239.0 389.1L239.3 396.3L142.5 400.2L142.5 394.1L0.0 394.1L14.5 381.5L24.0 360.2L33.1 347.2L39.8 329.1L42.3 314.6L43.6 302.7L41.8 274.1L38.4 262.5L29.4 242.9L20.0 214.2L20.9 209.8L25.6 203.1L27.8 196.4L25.5 181.2L21.9 172.6L28.5 165.6L39.3 144.3L42.6 122.4L40.2 110.0L51.9 105.1L53.5 88.8L55.5 87.8L58.8 89.3L63.1 82.5L67.4 85.4L71.8 84.4L76.1 74.2L79.7 71.7L85.1 60.7L87.5 61.3L89.9 58.3L91.6 60.4L89.2 63.5L89.4 66.0L88.3 63.8L85.5 66.6L88.9 75.0L86.7 75.3L84.9 78.9L86.2 80.2L86.3 86.7L84.7 87.2L83.3 91.0L82.8 96.1L83.6 101.7L86.9 102.4L91.2 92.0L91.4 90.3L89.0 89.8L92.1 81.9L95.3 80.2L96.0 83.2L94.5 84.0L93.4 93.7L89.6 97.5L88.1 103.2L90.8 104.6L93.2 102.5L93.6 98.9L96.1 96.8L100.9 86.2L103.0 72.5L101.4 55.8L102.6 52.2L114.9 43.2L118.7 42.3L126.5 43.2L134.8 40.0L134.3 37.1L130.2 36.3L130.3 37.4L125.9 35.6L122.6 31.5L121.1 27.0L120.9 20.6L123.5 16.2L128.3 13.4L133.0 6.8L130.1 3.8L123.4 2.4L142.5 4.4L144.3 2.9L144.3 0.2L148.1 0.2L148.9 2.3L166.8 13.8L169.2 12.1L172.8 13.5L176.1 12.6L184.2 15.6L190.3 22.9L192.6 29.5L203.7 29.9L213.1 37.3L219.0 37.9L227.7 44.0L235.5 43.7L242.8 51.9L240.9 55.4L244.3 62.7L247.7 65.2L251.2 76.7L241.8 72.3L237.9 76.2L239.6 78.5L238.2 80.3L239.4 86.0L245.1 90.7L247.9 90.9L247.3 94.0L249.5 98.5L249.0 104.6L250.7 107.8L247.8 118.3L246.5 144.8L243.6 146.1L238.6 153.5L237.2 154.1L238.5 151.4L236.2 150.8L232.0 153.9L228.8 172.8L224.1 173.2L222.0 174.8L222.0 178.9L211.5 179.4L208.0 182.3L205.3 188.2L204.6 199.1L202.6 202.7L206.7 211.2L215.7 214.0L223.0 218.3L231.9 206.8L236.8 205.4L241.0 197.2L239.8 196.2L236.1 198.7L238.3 191.9L241.5 193.9L246.5 190.0L245.9 187.7L241.7 186.5L248.4 185.9L251.5 180.9L265.3 178.0L268.3 173.9L272.3 171.8L275.9 171.4L284.6 176.0L288.4 179.4L295.9 194.9L298.4 218.4L302.5 233.3L303.7 253.9L306.3 265.7L310.7 274.9Z'
</script>

<template>
  <svg
    :width="size"
    :height="typeof size === 'number' ? size * 1.285 : size"
    viewBox="0 0 311.4 400.2"
    role="img"
    :aria-label="title"
    xmlns="http://www.w3.org/2000/svg"
  >
    <title>{{ title }}</title>
    <path
      :d="LP_PATH"
      :fill="fill"
      :stroke="stroke"
      :stroke-width="strokeWidth"
      stroke-linejoin="round"
    />
  </svg>
</template>
